import { Widget } from '@/components/widget';
import { useListener } from '@/hooks/use-listener';
import { useTheme } from '@/hooks/use-theme';
import React, { Suspense, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useGetAgent } from '@/features/agents/hooks/use-get-agent';

const DiffEditor = React.lazy(async () => {
  const module = await import('@monaco-editor/react');
  return { default: module.DiffEditor };
});

export function DiffEditorWidget({ id }: { id: string }) {
  const location = useLocation();
  const uuid = location.search.split('uuid=')[1];
  const { data: agentData } = useGetAgent(uuid!);
  const [sourceCode, setSourceCode] = useState<{
    previousContent: string;
    content: string;
    language: string;
  }>({
    previousContent: '',
    content: '',
    language: '',
  });
  const [state, setState] = useState<
    'loading' | 'success' | 'pending' | 'error'
  >('success');
  const { theme } = useTheme('light');

  const showEditor = state === 'success' || state === 'pending';

  useListener({
    id,
    listenerFn: (data) => {
      if (data?.sourceCode) {
        setSourceCode(data.sourceCode);
      }
      setState('success');
    },
    type: 'source-code/success',
  });

  useListener({
    id,
    listenerFn: () => {
      setState('loading');
    },
    type: 'source-code/loading',
  });

  return (
    <Widget title="Diff View" id={id}>
      <div className="h-full min-h-[600px]">
        <Suspense fallback={<div className="dark:text-white">Loading...</div>}>
          {showEditor ? (
            sourceCode?.previousContent ? (
              <DiffEditor
                originalLanguage={
                  sourceCode.language || agentData?.codeLanguage
                }
                original={
                  sourceCode?.previousContent?.trim() || agentData?.codePrevious
                }
                modifiedLanguage={
                  sourceCode.language || agentData?.codeLanguage
                }
                modified={sourceCode?.content?.trim() || agentData?.code}
                theme={theme === 'dark' ? 'vs-dark' : 'vs-light'}
              />
            ) : (
              <p className="dark:text-white">No changes to compare.</p>
            )
          ) : (
            <div className="dark:text-white">Loading...</div>
          )}
        </Suspense>
      </div>
    </Widget>
  );
}
